import { Component, Inject, Input } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose
} from '@angular/material/dialog';
import { Delivery, DepotLocation } from '../../../models';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import moment from 'moment';
import { DeliveryService } from '../../services/delivery.service';
import { ConfigService } from '../../services/config.service';
import { DeliveryItemNamePipe } from '../../pipes/delivery-item-name.pipe';
import { CustomerNamePipe } from '../../pipes/customer-name.pipe';
import { MatButton } from '@angular/material/button';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker
} from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { FlexModule } from '@angular/flex-layout';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatSuffix
} from '@angular/material/form-field';
import { TestIdDirective } from '../../directives/test-id.directive';
import { AlertTextComponent } from '../../alert-text/alert-text.component';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { format, parse } from 'date-fns';
import {
  NgxMatTimepickerComponent,
  NgxMatTimepickerModule
} from 'ngx-mat-timepicker';
import { MatIcon } from '@angular/material/icon';
import { catchError, of, take, Observable, map } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { BookingItemNamePipe } from '../../pipes/booking-item-name.pipe';

const completeingOnHireCollectionWarning = `By completing this collection, any of the items below that are still on hire will be taken off hire, and the off hire date will be set to the delivery completed date.`;
const completingBookingDeliveryWarning = `By completing this delivery, the items below will be placed on a hire contract, the on hire date will be set to the delivery completed date.`;

@Component({
  selector: 'complete-delivery-dialog',
  templateUrl: './complete-delivery-dialog.component.html',
  styleUrls: ['./complete-delivery-dialog.component.css'],
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    NgIf,
    AlertTextComponent,
    TestIdDirective,
    NgFor,
    ReactiveFormsModule,
    MatFormField,
    FlexModule,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    CustomerNamePipe,
    NgxMatTimepickerModule,
    MatLabel,
    MatError,
    MatIcon,
    MatSuffix,
    AsyncPipe
  ],
  providers: [DeliveryItemNamePipe, BookingItemNamePipe, DeliveryService]
})
export class CompleteDeliveryDialogComponent {
  @Input() delivery: Delivery;
  form = new UntypedFormGroup({
    completed_date: new UntypedFormControl(moment(), Validators.required),
    completed_time: new UntypedFormControl(
      format(new Date(), 'HH:mm'),
      Validators.required
    )
  });
  currentLocation: DepotLocation;
  warningMessage: string;
  items: Observable<string[]>;

  constructor(
    public dialogRef: MatDialogRef<CompleteDeliveryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    private deliveryService: DeliveryService,
    private configService: ConfigService,
    private deliveryNamePipe: DeliveryItemNamePipe,
    private toastr: ToastrService
  ) {
    if ('delivery' in data) {
      this.delivery = data.delivery;
      if (
        this.delivery.type === 'collection' &&
        this.delivery.deliverable_type === 'docket'
      ) {
        this.warningMessage = completeingOnHireCollectionWarning;
      }
      if (
        this.delivery.type === 'delivery' &&
        this.delivery.deliverable_type === 'booking'
      ) {
        this.warningMessage = completingBookingDeliveryWarning;
      }
      this.getItems(this.delivery.id);
    }
    this.configService.currentLocation.subscribe((location) => {
      if (location) {
        this.currentLocation = location;
      }
    });
  }

  getItems(deliveryId: number) {
    if ('deliveryNoteLines' in this.data) {
      this.items = of(this.data.deliveryNoteLines);
    } else {
      this.items = this.deliveryService
        .getDeliveryItems(deliveryId)
        .pipe(
          map((response) => response.deliveryItems.map((item) => item.name))
        );
    }
  }

  save() {
    const completedDateTime = parse(
      this.form.value.completed_time,
      'HH:mm',
      this.form.value.completed_date.toDate()
    );

    this.deliveryService
      .complete(this.delivery.id, {
        fulfilled_date: completedDateTime.toISOString(),
        location: this.currentLocation.id
      })
      .pipe(
        take(1),
        catchError((error) => {
          console.log({ error });
          if (error.error.error) {
            this.toastr.warning(error.error.error);
          } else {
            this.toastr.error(
              'An error occurred attempting to complete the delivery'
            );
          }
          return of(null);
        })
      )
      .subscribe((result) => {
        if (result) {
          this.dialogRef.close(true);
        }
      });
  }

  updateTime(event: any, field: string) {
    this.form.get(field).patchValue(event);
  }

  updateTimeEnteredManually(field: string, picker: NgxMatTimepickerComponent) {
    const control = this.form.get(field);
    if (!control.value) {
      return;
    }
    // 24 hr patterns to try and parse
    const parsePatterns = ['HH:mm', 'HH.mm', 'H:mm', 'HHmm', 'Hmm', 'HH', 'H'];
    const invalidValue = control.value;
    for (const pattern of parsePatterns) {
      const parsed = parse(invalidValue, pattern, new Date());
      if (parsed.toString() !== 'Invalid Date') {
        control.patchValue(format(parsed, 'HH:mm'));
        return picker.updateTime(control.value);
      }
    }
    control.setErrors({ invalidTime: true });
  }
}
